/* eslint-disable no-unused-vars */
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Stack, Tooltip } from '@mui/material';
import Countdown from 'on-react-countdown';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LogoPlaceholder from '../../../../../assets/images/logo-placeholder.jpg';
import {
	BASE_URLS,
	CURRENCY,
	IS_SA,
	MIN_INVESTMENT,
	WEBSITE_URL,
} from '../../../../../config/appConfig';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { MarketplaceStatuses } from '../../../../../constants/marketplaceStatuses';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { currencyFormatterHelper } from '../../../../../helpers/currencyFormatterHelper';
import { checkImageRatio } from '../../../../../helpers/imageRatioHelper';
import { buildLocalizeRoute } from '../../../../../helpers/languageHelper';
import { Observables } from '../../../../../observables';
import { getUser } from '../../../../../services/authService';
import { LanguageService } from '../../../../../services/languageService';
import { SystemSettingsService } from '../../../../../services/systemSettingsService';
import { Section } from '../../../../../shared/components/common/section';
import { SummaryCard } from '../../../../../shared/components/common/summaryCard';
import { CustomTooltip } from '../../../../../shared/components/common/tooltip';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';
import { CustomProgressBar, PROGRESS_TOOLTIP_SLOT_PROPS } from '../../../../../shared/components/formUtilities/useProgressBar';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import { MarketplaceDetailLoader } from '../../../../../shared/components/investors/loaders/marketplaceDetailLoader';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { InvestorService } from '../../../investorService';
import { PreventDepositDialog } from '../../transfers/deposit/preventDepositDialog';
import { InvestBehalfDialog } from './marketplaceInvestDialog/investBehalfDialog';
import { MarketplaceService } from '../marketplaceService';
import { BusinessFinancialRatios } from './businessFinancialRatios';
import { LatestOpportunities } from '../../../latestOpportunities';
import { InvestErrorDialog } from './investErrorDialog';
import { InvestSuccessDialog } from './investSuccessDialog';
import PeopleIcon from '@mui/icons-material/People';
import { formatGTMUserInfo, pushToDataLayer } from '../../../../../helpers/pushToGTMHelper';
import { identifyOpenPanel, trackOpenPanelView } from '../../../../../helpers/openPanelHelper';
import { GTMEvents } from '../../../../../constants/gtmEvents';
import { OpenPanelCategories, OpenPanelEvents } from '../../../../../constants/openPanelEvents';
import { LoanStatuses } from '../../../../../constants/loanStatus';

export const MarketplaceDetails = () => {
	const marketplaceService = new MarketplaceService();
	const investorService = new InvestorService();
	const languageService = new LanguageService();
	const systemSettingsService = new SystemSettingsService();
	const [availableCash, setAvailableCash] = useState(0);
	const [businessDetails, setBusinessDetails] = useState({});
	const [openInvest, setOpenInvest] = useState(false);
	const [isOpenOTPDialog, setIsOpenOTPDialog] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [status, setStatus] = useState(searchParams.get('status'));
	const [investDetails, setInvestDetails] = useState({});
	const [investedAmount, setInvestedAmount] = useState(0);
	const [couponDetails, setCouponDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isOpenInvestSuccess, setIsOpenInvestSuccess] = useState(false);
	const [isOpenInvestError, setIsOpenInvestError] = useState(false);
	const [investErrorMessage, setInvestErrorMessage] = useState(false);
	const [isImageRatioFit, setisImageRatioFit] = useState(false);
	const [loadingAvailableCash, setLoadingAvailableCash] = useState(false);
	const [timeRemaining, setTimeRemaining] = useState(false);
	const [showTransferPopup, setShowTransferPopup] = useState(false);
	const [limitsOnDueDiligence, setLimitsOnDueDiligence] = useState(null);
	const params = useParams();
	const [bucketId, setBucketId] = useState(params?.id);
	const navigate = useNavigate();
	const { t } = useTranslation();

	const isApproved = () => getUser()?.isApproved;

	useEffect(() => {
		setBucketId(params?.id);
		setStatus(searchParams.get('status'));
		getMarketplaceDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [params?.id]);

	useEffect(() => {
		checkImageRatio(businessDetails?.logo).then((resp) => {
			setisImageRatioFit(resp);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [businessDetails]);

	const opportunityViewTracked = useRef(false);
	useEffect(() => {
		const opportunityName = businessDetails?.businessName || businessDetails?.industry;
		if (!opportunityViewTracked.current && opportunityName) {
			opportunityViewTracked.current = true;
			trackOpenPanelView(OpenPanelEvents.opportunitiesDetailsViewed, {
				opportunity_name: opportunityName,
				category: OpenPanelCategories.opportunities,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [businessDetails]);

	const isTimeRemaining = () => {
		return timeRemaining > 0 ? true : false;
	};

	const isHistory = () => {
		return MarketplaceStatuses.Past === status;
	};

	const calculateMinAmount = () => {
		const availableLoans =
			businessDetails?.loans?.filter(
				(l) => l.loanAmount - l.investedAmount > 0
			) || [];
		return availableLoans.length * MIN_INVESTMENT;
	};

	const getPercentage = (item) => {
		if (!isHistory()) {
			let investPercentage =
				(businessDetails?.investedAmount / businessDetails?.campaignAmount) * 100;
			let formattedValue = parseFloat(
				(Math.floor(investPercentage * 100) / 100).toString().substring(0, 4)
			);
			return formattedValue > 100 ? 100 : formattedValue;
		}
		return 100;
	};

	const getMarketplaceDetails = () => {
		setLoading(true);
		marketplaceService
			.getMarketplaceDetails(bucketId)
			.then((resp) => {
				setBusinessDetails(resp.data);
				let timeRemaining = resp.data.timeRemaining;
				let days = 0;
				let fakeDays = 0;
				let daySeconds = 0;
				if (timeRemaining) {
					days = timeRemaining / 86400;
					fakeDays = days % 14;
					daySeconds = fakeDays * 86400;
				}
				setTimeRemaining(daySeconds ? daySeconds : businessDetails.timeRemaining);
				investorSummaryShort();
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const investorSummaryShort = () => {
		setLoadingAvailableCash(true);
		investorService
			.getInvestorSummaryShort()
			.then((response) => {
				setAvailableCash(response.data?.availableCash);
				if (typeof response.data?.availableCash === 'number') {
					identifyOpenPanel(getUser(), { available_funds: response.data.availableCash });
				}
				setLoadingAvailableCash(false);
			})
			.catch((err) => {
				setLoadingAvailableCash(false);
			});
	};

	const getRemainingAmount = () => {
		return +businessDetails?.campaignAmount - +businessDetails?.investedAmount;
	};

	const settings = () => {
		return systemSettingsService.systemSettings();
	};

	const isEnableInvestButton = () => {
		return (
			isTimeRemaining() &&
			settings()?.enableMarketplace &&
			MarketplaceStatuses.Current === status &&
			businessDetails?.campaignAmount > businessDetails?.investedAmount
		);
	};

	const onInvest = () => {
		if (!isApproved()) {
			setShowTransferPopup(true);
			return;
		}

		if (availableCash !== 0) {
			setOpenInvest(true);
		} else {
			navigate(
				buildLocalizeRoute(
					`${AppRoutes.Investor}/${AppRoutes.ITransfer}/${AppRoutes.IDeposit}`
				)
			);
		}
	};

	const onInvestment = (form) => {
		setLoading(true);
		setOpenInvest(false);
		setInvestedAmount(form?.amount);
		if (form?.couponDetails) {
			let couponDetails = JSON.parse(JSON.stringify(form?.couponDetails)) || null;
			setCouponDetails(couponDetails);
			delete form.couponDetails;
		}
		marketplaceService
			.investMarketplace(bucketId, form)
			.then((resp) => {
				if (resp.data) {
					pushToDataLayer(GTMEvents.investmentConfirmed,
						{
							opportunityName: businessDetails?.businessName,
							value: form.amount,
							...formatGTMUserInfo()
						}
					);
					setLoading(false);
					setInvestDetails(resp.data);
					if (resp.data?.otpRequested) {
						setIsOpenOTPDialog(true);
					} else {
						onSuccessInvest(resp.data);
					}
				}
			})
			.catch((err) => {
				const errors = err?.response?.data?.errors || err?.response?.data;
				if (errors) {
					const key = Object.keys(errors)[0];
					setInvestErrorMessage(errors[key][0] || '');
				} else {
					setInvestErrorMessage('Something went wrong. Please try again');
				}

				setLoading(false);
				setTimeout(() => {
					setIsOpenInvestError(true);
				});
			});
	};

	const onSuccessInvest = (marketPlaceDetails = null) => {
		setIsOpenInvestSuccess(true);
		setBusinessDetails(
			marketPlaceDetails
				? marketPlaceDetails
				: investDetails?.marketPlaceDetails
		);
	};

	const onCloseOTPAuth = (value) => {
		setIsOpenOTPDialog(false);
		if (value) {
			onSuccessInvest();
		}
	};

	const onSuccessInvestClose = (e) => {
		let baseRoute = `${BASE_URLS[IS_SA ? 'sa' : 'en']}/${languageService.isArabic() ? 'ar' : 'en'}`;
		let route = `${baseRoute}/${AppRoutes.Investor}/${AppRoutes.MyInvestments}`;
		window.open(route, "_self");
	};

	const isFundraising = () => {
		return businessDetails?.loanStatus === LoanStatuses.Fundraising;
	};

	const getBorrowerFee = () => {
		let originationFee = businessDetails?.originationFee * 100;
		return {
			costToBorrower: (+originationFee + +businessDetails?.profit) || 0,
			profit: businessDetails?.profit || 0,
			originationFee: originationFee
		};

	};

	return (
		<Box className="grid grid-flow-row gap-6">
			{!loading ? (
				<>
					{/* ===== HERO: 2-column layout (details left, image right) ===== */}
					<Box className="grid grid-cols-1 md:grid-cols-3 gap-6">
						{/* ---- LEFT COLUMN: Details ---- */}
						<Box className="md:col-span-2 py-6 px-4 md:py-10 md:px-12 bg-gray-100 rounded-3xl">
							<Stack gap={1} direction="column">
								{/* Business Name */}
								{isHistory() ? (
									<Box className="blur-sm text-investor-deep text-[32px] font-bold">XXXX XXX XXX</Box>
								) : (
									<Box className="text-investor-deep text-[32px] font-bold leading-tight">
										{!IS_SA ? businessDetails?.businessName : businessDetails?.industry || ''}
									</Box>
								)}

								{/* Description */}
								{businessDetails?.description ? (
									!isHistory() ? (
										<Box className="text-investor-deep">
											<p
												dangerouslySetInnerHTML={{
													__html: `${businessDetails?.description}`,
												}}
												className="!sub-title-text !text-investor-deep"
											/>
										</Box>
									) : (
										<Box className="blur-sm text-investor-deep">
											XXXXX XXXX XXX : XXXXXXXX
											XXXXX XXXX XXX : XXXXXXXX
											XXXXX XXXX XXX : XXXXXXXX
										</Box>
									)
								) : null}

								{/* Funded / Requested row */}
								<Box className="pt-6">
									<Box className="flex justify-between items-end">
										<Box>
											<Box className="text-sm text-investor-deep">{t('funded')}</Box>
											<Box className="font-bold text-[20px] text-investor-deep">
												<span className="currency-symbol">{CURRENCY}</span>{' '}
												{currencyFormatterHelper(businessDetails?.investedAmount, 0)}
											</Box>
										</Box>
										<Box className="text-right">
											<Box className="text-sm text-investor-deep">{t('requested')}</Box>
											<Box className="font-bold text-[20px] text-investor-deep">
												<span className="currency-symbol">{CURRENCY}</span>{' '}
												{currencyFormatterHelper(businessDetails?.campaignAmount, 0)}
											</Box>
										</Box>
									</Box>
									<Box className="flex items-center justify-between mt-2">
										<Box className="w-2 h-2 rounded-full bg-investor-deep flex-shrink-0"></Box>
										<Box className="flex-1 border-t-2 border-dashed border-investor-deep mx-1"></Box>
										<Box className="w-2 h-2 rounded-full bg-investor-deep flex-shrink-0"></Box>
									</Box>
								</Box>

								{/* Progress Bar */}
								<Box className="pt-4 pb-6">
									<Tooltip
										title={getRemainingAmount() > 0
											? `${t('remaining')}: ${CURRENCY} ${currencyFormatterHelper(getRemainingAmount())}`
											: ''
										}
										arrow
										enterTouchDelay={0}
										leaveTouchDelay={3000}
										placement="top"
										slotProps={PROGRESS_TOOLTIP_SLOT_PROPS}
									>
										<Box>
											<CustomProgressBar
												value={getPercentage()}
												bgColor="#00D175"
												baseBgColor="#96ECE0"
												labelColor="#063236"
												labelSize="32px"
												paddingY="24px"
												borderRadius="10px"
											/>
										</Box>
									</Tooltip>
								</Box>

								{/* Co-fund CTA Strip */}
								<Box
									className={`flex items-center justify-between rounded-full bg-investor-bg p-1.5 ${languageService.isArabic() ? "pr-5" : "pl-5"}`}
								>
									<Box className="text-white text-sm">
										{t('myOrdersIn')}{' '}
										{t('are')}{' '}
										<strong>
											<span className="currency-symbol">{CURRENCY}</span>{' '}
											{currencyFormatterHelper(businessDetails?.personalPartsAmount)}
										</strong>
									</Box>
									{isEnableInvestButton() ? (
										<ButtonWrapper
											disabled={!isFundraising()}
											loading={loadingAvailableCash}
											onClick={() => {
												if (isFundraising()) {
													onInvest();
												}
											}}
											startIcon={<ArrowForwardIcon className={`text-investor-deep ${languageService.isArabic() ? "scale-x-[-1]" : ""}`} />}
											classList="!bg-investor-green !text-investor-deep !border-investor-green !font-bold !rounded-full !px-6 !py-2 !min-w-0"
											label={isFundraising() ? t('investNow') : t('comingSoon')}
										/>
									) : null}
								</Box>

								{/* Financial statements download — hidden per design
									{businessDetails?.borrowerFinancialStatements && Array.isArray(businessDetails.borrowerFinancialStatements) && businessDetails.borrowerFinancialStatements.length > 0 && !isHistory() ? (
										<Box className="flex flex-col gap-2 mt-2">
											{businessDetails.borrowerFinancialStatements.map((statement, index) => (
												<ButtonWrapper
													key={index}
													size="small"
													classList="!min-w-0 !px-3 !py-1 !text-[12px] !w-fit"
													startIcon={<DownloadIcon className="!text-[16px]" />}
													onClick={() => window.open(statement.url, '_blank')}
													label={`Download Financial Statement ${index + 1}`}
												/>
											))}
										</Box>
									) : null}
									*/}

								{businessDetails?.isFsParticipating && !isHistory() ? (
									<Box className="mt-1">
										<span className="text-sm !font-semibold text-investor-deep">
											{t('fundingSouqFunded')}
										</span>
									</Box>
								) : null}

								{/* Key Metrics Row */}
								<Box className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mt-4">
									{/* Net Yield */}
									<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
										<TextWrapper
											classList="!font-[500] !text-investor-deep"
											type="caption"
											text={
												<span>
													<span>{t('roi')}</span>
													<CustomTooltip id="netYieldInfoId2" text="netYieldInfo" />
												</span>
											}
										/>
										<TextWrapper
											classList="!font-bold !text-sm !text-investor-deep"
											text={`${businessDetails?.fixed}%`}
										/>
									</Box>

									{/* Repayments */}
									<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
										<TextWrapper
											classList="!font-[500] !text-investor-deep"
											type="caption"
											text={t('repayments')}
										/>
										<TextWrapper
											classList="!font-bold !text-sm !text-investor-deep"
											text={`${businessDetails?.paymentFrequency}`}
										/>
									</Box>

									{/* IRR */}
									<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
										<TextWrapper
											classList="!font-[500] !text-investor-deep"
											type="caption"
											text={
												<span>
													<span>{t('irr')}</span>
													<CustomTooltip id="irrInfoId2" text="irrInfo" />
												</span>
											}
										/>
										<TextWrapper
											classList="!font-bold !text-sm !text-investor-deep"
											text={`${businessDetails?.investorIRR || '25.9'}%`}
										/>
									</Box>

									{/* Rating */}
									<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
										<TextWrapper
											classList="!font-[500] !text-investor-deep"
											type="caption"
											text={t('rating')}
										/>
										<Box
											className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm text-investor-deep bg-investor-green border-2 border-investor-deep"
										>
											{businessDetails?.rating}
										</Box>
									</Box>

									{/* Tenure */}
									<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
										<TextWrapper
											classList="!font-[500] !text-investor-deep"
											type="caption"
											text={t('tenure')}
										/>
										<TextWrapper
											classList="!font-bold !text-sm !text-investor-deep"
											text={`${businessDetails?.month} ${t('monthsLowerCase')}`}
										/>
									</Box>

									{businessDetails?.businessToGrow ? (
										<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
											<TextWrapper
												classList="!font-[500] !text-investor-deep"
												type="caption"
												text={
													<span>
														<span>{t('growthForecast')}</span>
														<CustomTooltip id="growthForecastTooltip" text="growthForecastTooltip" />
													</span>
												}
											/>
											<TextWrapper
												classList="!font-bold !text-sm !text-investor-deep"
												text={`${businessDetails?.businessToGrow}`}
											/>
										</Box>
									) : null}
									{IS_SA ? (
										<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
											<TextWrapper
												classList="!font-[500] !text-investor-deep"
												type="caption"
												text={t('costToBorrower')}
											/>
											<TextWrapper
												classList="!font-bold !text-sm !text-investor-deep"
												text={`${getBorrowerFee()?.costToBorrower}%`}
											/>
										</Box>
									) : null}
									{IS_SA ? (
										<Box className="flex justify-between items-center py-3 border-b border-investor-deep">
											<TextWrapper
												classList="!font-[500] !text-investor-deep"
												type="caption"
												text={t('originationFee')}
											/>
											<TextWrapper
												classList="!font-bold !text-sm !text-investor-deep"
												text={`${getBorrowerFee()?.originationFee}%`}
											/>
										</Box>
									) : null}
								</Box>
							</Stack>
						</Box>

						{/* ---- RIGHT COLUMN: Image + Business Info ---- */}
						<Box className="flex flex-col rounded-3xl overflow-hidden">
							{/* Logo Image */}
							<Box className="overflow-hidden h-64 md:h-[40rem] rounded-t-3xl">
								<img
									className={`w-full h-full object-contain ${isHistory() ? 'blur-md' : ''}`}
									src={
										isHistory()
											? LogoPlaceholder
											: businessDetails?.logo
												? businessDetails?.logo
												: LogoPlaceholder
									}
									alt="Marketplace Logo"
								/>
							</Box>

							{/* Business Info Sidebar */}
							<Box className="flex-1 p-6 bg-investor-mint rounded-3xl -mt-10 relative">
								{/* Sector */}
								<Box className="flex flex-col py-3 border-b border-investor-deep">
									<TextWrapper
										classList="!text-black"
										type="caption"
										text={t('industry')}
									/>
									<TextWrapper
										classList="!font-bold !text-xl !text-black"
										text={`${businessDetails?.industry || '---'}`}
									/>
								</Box>

								{/* Year in Business */}
								<Box className="flex flex-col py-3 border-b border-investor-deep">
									<TextWrapper
										classList="!text-black"
										type="caption"
										text={t('yearsTrading')}
									/>
									<TextWrapper
										classList="!font-bold !text-xl !text-black"
										text={`${businessDetails?.yearsTrading || '---'}`}
									/>
								</Box>

								{/* Country */}
								<Box className="flex flex-col py-3">
									<TextWrapper
										classList="!text-black"
										type="caption"
										text={t('country')}
									/>
									<TextWrapper
										classList="!font-bold !text-xl !text-black"
										text={`${businessDetails?.region || '---'}`}
									/>
								</Box>
							</Box>
						</Box>
					</Box>

					{/* ===== BOTTOM: 2-col layout (Financial Highlights left, Latest Opportunities + Disclaimers right) ===== */}
					<Box className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
						{/* Financial Highlights - left col */}
						<Box className="md:col-span-2">
							{businessDetails?.borrowerId ? (
								<BusinessFinancialRatios
									businessDetails={businessDetails}
									onLoaded={({ limitsOnDueDiligence: val }) => setLimitsOnDueDiligence(val)}></BusinessFinancialRatios>
							) : null}
						</Box>

						{/* Latest Opportunities + Disclaimers - right col */}
						<Box className="md:col-span-1 flex flex-col gap-4">
							<LatestOpportunities />

							{!IS_SA ? (
								<Box className="flex flex-col gap-2 text-xs text-investor-deep">
									<Box>
										<span>{t('limitsOnDueDiligence')}: </span>
										<span className="font-bold">{limitsOnDueDiligence || t('notApplicable')}</span>
									</Box>
									{businessDetails?.rating === 'A' ? (
										<Box>{t('shamsMoreInfo')}</Box>
									) : null}
									<Box>{t('businessListingDisclosure')}</Box>
									<Box>
										{t('allDataIsProvidedByBusiness1')}&nbsp;
										<a
											className="underline-link !text-xs"
											target="_blank"
											rel="noreferrer"
											href={`${WEBSITE_URL}en/credit-assessment`}>
											{t('here')}
										</a>
										.&nbsp;{t('allDataIsProvidedByBusiness2')}
										<Box className="h-2"></Box>
										{t('allDataIsProvidedByBusiness')} {t('assignedRisk')}{' '}
										{t('approvedDFSA')}
										<Box className="h-2"></Box>
										{t('pleaseNoteYearToDate')}
									</Box>
								</Box>
							) : (
								<Box className="text-xs text-investor-deep">{t('financialStatementDisclosure')}</Box>
							)}
						</Box>
					</Box>
					<InvestBehalfDialog
						itemDetails={businessDetails}
						onInvestment={(form) => {
							onInvestment(form);
						}}
						remainingAmount={getRemainingAmount()}
						minInvestmentAmount={calculateMinAmount()}
						state={openInvest}
						onClose={(e) => {
							setOpenInvest(false);
							pushToDataLayer(GTMEvents.investmentAbandoned,
								{ ...formatGTMUserInfo() }
							);
						}}></InvestBehalfDialog>
					{isOpenOTPDialog ? (
						<OTPAuthenticationDialog
							onClose={(e) => {
								onCloseOTPAuth(e);
							}}
							type={OTPTypes.Transaction}
							corrolationId={investDetails?.correlationId}
							state={isOpenOTPDialog}></OTPAuthenticationDialog>
					) : null}
					{isOpenInvestSuccess ? (
						<InvestSuccessDialog
							couponDetails={couponDetails}
							amount={investedAmount}
							state={isOpenInvestSuccess}
							onClose={(e) => {
								onSuccessInvestClose(e);
							}}></InvestSuccessDialog>
					) : null}
					{isOpenInvestError ? (
						<InvestErrorDialog
							amount={investedAmount}
							errorMessage={investErrorMessage}
							state={isOpenInvestError}
							onClose={() => {
								setIsOpenInvestError(false);
							}}></InvestErrorDialog>
					) : null}
					{showTransferPopup ?
						<PreventDepositDialog
							onClose={() => {
								setShowTransferPopup(false);
							}} state={showTransferPopup} /> : null}
				</>
			) : (
				<MarketplaceDetailLoader></MarketplaceDetailLoader>
			)}
		</Box>
	);
};
